import { useForm } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { common, pick } from "../../lib/i18n";
import type { AgeBand, BodyFocus, CurrentUser, HairStyling, HandsStyling, ModelPersonaDetail, ModelPersonaOptions, PersonaPresentation, ProductSummary } from "../../types";

const copy = {
  pl: {
    newPersona: "Nowa persona", editPersona: (n?: string) => `Edytuj: ${n}`,
    basicInfo: "Podstawowe informacje", personaName: "Nazwa presetu", personaNamePlaceholder: "np. Warm medium tone, hands close-up",
    skinTone: "Odcień skóry (skala Google Monk)", ageBand: "Przedział wiekowy", presentation: "Prezentacja",
    handsStyling: "Stylizacja dłoni", hairStyling: "Stylizacja włosów", bodyFocus: "Kadr / focus",
    appearanceNotesOpt: "Dodatkowe cechy wyglądu (opcjonalne)",
    appearanceNotesPlaceholder: "Neutralne deskryptory wyglądu, np. smukłe dłonie, krótkie paznokcie...",
    appearanceNotesNote: "Trafia bezpośrednio do promptu generacji jako opis wyglądu osoby.",
    activeLabel: "Aktywna (widoczna w formularzu generacji)",
    createPersona: "Utwórz personę", saveChanges: "Zapisz zmiany",
    ageBandLabels: { "18_25": "18–25 lat", "25_35": "25–35 lat", "35_50": "35–50 lat", "50_65": "50–65 lat", "65_plus": "65+ lat" } as Record<string, string>,
    presentationLabels: { female: "Kobieca", male: "Męska", neutral: "Neutralna", unspecified: "Nieokreślona" } as Record<string, string>,
    handsStylingLabels: { natural_short: "Naturalne, krótkie paznokcie", glossy_manicure: "Błyszczący manicure", bare: "Bez stylizacji" } as Record<string, string>,
    hairStylingLabels: { up: "Upięte", down: "Rozpuszczone", pulled_back: "Związane z tyłu" } as Record<string, string>,
    bodyFocusLabels: { finger: "Dłoń / palec", ear: "Ucho", wrist: "Nadgarstek", neck_decolletage: "Szyja / dekolt", full: "Cała sylwetka" } as Record<string, string>,
  },
  en: {
    newPersona: "New persona", editPersona: (n?: string) => `Edit: ${n}`,
    basicInfo: "Basic information", personaName: "Preset name", personaNamePlaceholder: "e.g. Warm medium tone, hands close-up",
    skinTone: "Skin tone (Google Monk Scale)", ageBand: "Age band", presentation: "Presentation",
    handsStyling: "Hands styling", hairStyling: "Hair styling", bodyFocus: "Framing / focus",
    appearanceNotesOpt: "Additional appearance notes (optional)",
    appearanceNotesPlaceholder: "Neutral appearance descriptors, e.g. slender hands, short nails...",
    appearanceNotesNote: "Goes directly into the generation prompt as the person's appearance description.",
    activeLabel: "Active (visible in the generation form)",
    createPersona: "Create persona", saveChanges: "Save changes",
    ageBandLabels: { "18_25": "18–25 y/o", "25_35": "25–35 y/o", "35_50": "35–50 y/o", "50_65": "50–65 y/o", "65_plus": "65+ y/o" } as Record<string, string>,
    presentationLabels: { female: "Feminine", male: "Masculine", neutral: "Neutral", unspecified: "Unspecified" } as Record<string, string>,
    handsStylingLabels: { natural_short: "Natural, short nails", glossy_manicure: "Glossy manicure", bare: "No manicure styling" } as Record<string, string>,
    hairStylingLabels: { up: "Up", down: "Down", pulled_back: "Pulled back" } as Record<string, string>,
    bodyFocusLabels: { finger: "Hand / finger", ear: "Ear", wrist: "Wrist", neck_decolletage: "Neck / décolletage", full: "Full person" } as Record<string, string>,
  },
};

type Props = {
  persona: Partial<ModelPersonaDetail> & { id?: number; errors?: Record<string, string[]> };
  action: "create" | "update";
  options: ModelPersonaOptions;
  current_user: CurrentUser;
  products?: ProductSummary[];
};

export default function PersonasForm({ persona, action, options, current_user, products = [] }: Props) {
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const isNew    = action === "create";
  const t = pick(copy);
  const c = common();

  const { data, setData, post, patch, processing, errors } = useForm({
    name:             persona.name             ?? "",
    skin_tone:        persona.skin_tone        ?? 5,
    age_band:         persona.age_band         ?? options.age_bands[0],
    presentation:     persona.presentation     ?? options.presentations[0],
    hands_styling:    persona.hands_styling    ?? options.hands_stylings[0],
    hair_styling:     persona.hair_styling     ?? options.hair_stylings[0],
    body_focus:       persona.body_focus       ?? options.body_focuses[0],
    appearance_notes: persona.appearance_notes ?? "",
    active:           persona.active           ?? true,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNew) {
      post("/personas");
    } else {
      patch(`/personas/${persona.id}`);
    }
  };

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page" style={{ maxWidth: 640 }}>
        <div className="page-hd">
          <div>
            <h2>{isNew ? t.newPersona : t.editPersona(persona.name)}</h2>
          </div>
        </div>

        <form onSubmit={submit}>
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-hd"><h3>{t.basicInfo}</h3></div>
            <div className="panel-body">
              <div className="field">
                <label>{t.personaName}</label>
                <input
                  className="input"
                  type="text"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  placeholder={t.personaNamePlaceholder}
                />
                {errors.name && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.name}</span>}
              </div>

              <div className="field">
                <label>{t.skinTone}: {data.skin_tone}/10</label>
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={data.skin_tone}
                  onChange={(e) => setData("skin_tone", Number(e.target.value))}
                  style={{ width: "100%" }}
                />
                {errors.skin_tone && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.skin_tone}</span>}
              </div>

              <div className="field">
                <label>{t.ageBand}</label>
                <select className="input" value={data.age_band} onChange={(e) => setData("age_band", e.target.value as AgeBand)}>
                  {options.age_bands.map((band) => (
                    <option key={band} value={band}>{t.ageBandLabels[band] ?? band}</option>
                  ))}
                </select>
                {errors.age_band && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.age_band}</span>}
              </div>

              <div className="field">
                <label>{t.presentation}</label>
                <select className="input" value={data.presentation} onChange={(e) => setData("presentation", e.target.value as PersonaPresentation)}>
                  {options.presentations.map((value) => (
                    <option key={value} value={value}>{t.presentationLabels[value] ?? value}</option>
                  ))}
                </select>
                {errors.presentation && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.presentation}</span>}
              </div>

              <div className="field">
                <label>{t.handsStyling}</label>
                <select className="input" value={data.hands_styling} onChange={(e) => setData("hands_styling", e.target.value as HandsStyling)}>
                  {options.hands_stylings.map((value) => (
                    <option key={value} value={value}>{t.handsStylingLabels[value] ?? value}</option>
                  ))}
                </select>
                {errors.hands_styling && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.hands_styling}</span>}
              </div>

              <div className="field">
                <label>{t.hairStyling}</label>
                <select className="input" value={data.hair_styling} onChange={(e) => setData("hair_styling", e.target.value as HairStyling)}>
                  {options.hair_stylings.map((value) => (
                    <option key={value} value={value}>{t.hairStylingLabels[value] ?? value}</option>
                  ))}
                </select>
                {errors.hair_styling && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.hair_styling}</span>}
              </div>

              <div className="field">
                <label>{t.bodyFocus}</label>
                <select className="input" value={data.body_focus} onChange={(e) => setData("body_focus", e.target.value as BodyFocus)}>
                  {options.body_focuses.map((value) => (
                    <option key={value} value={value}>{t.bodyFocusLabels[value] ?? value}</option>
                  ))}
                </select>
                {errors.body_focus && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.body_focus}</span>}
              </div>

              <div className="field">
                <label>{t.appearanceNotesOpt}</label>
                <textarea
                  className="input"
                  rows={3}
                  style={{ height: "auto", padding: "8px 10px", resize: "vertical" }}
                  value={data.appearance_notes}
                  onChange={(e) => setData("appearance_notes", e.target.value)}
                  placeholder={t.appearanceNotesPlaceholder}
                />
                <span style={{ fontSize: 11, color: "var(--muted-2)", marginTop: 2 }}>
                  {t.appearanceNotesNote}
                </span>
                {errors.appearance_notes && <span style={{ fontSize: 11.5, color: "var(--error, #c0392b)" }}>{errors.appearance_notes}</span>}
              </div>

              <div className="field">
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={data.active}
                    onChange={(e) => setData("active", e.target.checked)}
                  />
                  {t.activeLabel}
                </label>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginBottom: 24 }}>
            <button type="button" className="btn btn-ghost" onClick={() => history.back()}>
              {c.actions.cancel}
            </button>
            <button type="submit" className="btn btn-primary" disabled={processing}>
              {isNew ? t.createPersona : t.saveChanges}
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
